import React, { useState, useEffect } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

import LogoName from '../Logo';
import ValueModelDisplay from './ValueModelDisplay';
import { fetchValueModelDisplay } from '../../actions/presentationAction';
import ValueModelSegmentsDisplay from './ValueModelSegmentsDisplay';
import ProductDisplay from './ProductDisplay';
import ExportChartToPptButton from '../helpers/ExportChartToPptButton';

const ViewPresentation = ({ presentation }) => {
    const [viewValueModel, setViewValueModel] = useState([]);

    useEffect(() => {
        // Ensure there are customer segments before proceeding
        if (!presentation.customer_segments || presentation.customer_segments.length === 0) {
            return;
        }

        const fetchData = async () => {
            try {
                // Loop over each customer segment and fetch its corresponding value model
                const results = await Promise.all(
                    presentation.customer_segments.map(segment =>
                        fetchValueModelDisplay(segment.value_model_id, segment.variables)
                    )
                );

                // Map to extract the desired value from each result
                const values = results.map(result => result.value);
                setViewValueModel(values);
            } catch (error) {
                console.error('Error fetching view value model:', error);
            }
        };

        fetchData();
    }, [presentation.customer_segments]);

    const startSlide = (presentation) => {
        return (
            <Typography variant="h5" sx={{ color: 'white' }} >
                {presentation.title}
            </Typography>
        )
    }

    const productSlide = (presentation) => {
        return (
            <Box>
                <ProductDisplay
                    presentation={presentation}
                />
            </Box>
        )
    }

    // Helper to render a slide for a given value model segments
    const valueModelSegmentsSlide = (presentation, valueModels) => {
        return (
            <Box sx={{ bgcolor: 'white' }}>
                <ValueModelSegmentsDisplay
                    presentation={presentation}
                    viewValueModel={valueModels}
                />
            </Box>
        );
    };

    // Helper to render a slide for a given value model
    const valueModelSlide = (presentation, viewModel) => {
        return (
            <Box sx={{ bgcolor: 'white' }}>
                <ValueModelDisplay
                    product={presentation.product}
                    viewValueModel={viewModel}
                />
            </Box>
        );
    };

    // Build slides array
    const slides = [
        // Always include the start slide
        {
            id: 1,
            heading: null,
            content: startSlide(presentation)
        },
        // Always include the product slide
        {
            id: 2,
            heading: 'Product',
            content: productSlide(presentation)
        },        
        // Conditionally add a slide that renders the segments view if "1" is included in presentation.slides
        ...(presentation.slides.includes("1")
            ? [
                {
                    id: 3,
                    heading: 'Value by Customer Segment',
                    content: valueModelSegmentsSlide(presentation, viewValueModel)
                }
            ]
            : []),
        // Conditionally add slides for each customer segment if "2" is included in presentation.slides
        ...(presentation.slides.includes("2")
            ? presentation.customer_segments.map((segment, index) => ({
                id: index + 4, // ensure unique id for each slide, shifted to avoid collisions
                heading: `Value for ${segment.name}`,
                content:
                    viewValueModel.length > index
                        ? valueModelSlide(presentation, viewValueModel[index])
                        : 'Loading...'
            }))
            : [])
    ];

    return (
        <Box>
            <Box sx={{ mb: 1, mr: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <ExportChartToPptButton
                    chartIds={slides.map(slide => `chart${slide.id}`)}
                />
            </Box>
            <Box
                sx={{
                    height: '80vh',         // Use full viewport height, or adjust as needed
                    overflowY: 'auto',       // Enable vertical scrolling
                    p: 2,                    // Padding around the content
                }}
            >
                <Stack>
                    {slides.map((slide) => (
                        <React.Fragment key={slide.id}>
                            <Paper
                                key={slide.id}
                                id={`chart${slide.id}`}
                                sx={{
                                    width: '100%',
                                    maxWidth: 1200,         // max width for the slide
                                    aspectRatio: '16/9',    // maintains 16:9 ratio (height will adjust accordingly)
                                    borderRadius: 0,        // rounded corners
                                    p: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: theme =>
                                        `linear-gradient(to bottom right, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                    position: 'relative',
                                    zIndex: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        left: 16,
                                        color: 'white'
                                    }}
                                >
                                    <Typography variant="h5" >
                                        {slide.heading}
                                    </Typography>
                                </Box>
                                {slide.content}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: 16,
                                    }}
                                >
                                    <LogoName />
                                </Box>
                            </Paper>
                            <Box sx={{ height: 10 }}></Box>
                        </React.Fragment>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

export default ViewPresentation;